import React from "react";
import QueryBuilder, { Field, RuleGroupType, RuleType } from "react-querybuilder";
import "react-querybuilder/dist/query-builder.css";
import { formatMessage } from "lib/globalize-util";
import { Theme, Fabric, Button } from "@nuorder/components";
import { CustomFieldSelector } from "./custom-components/custom-field-selector";
import { CustomValueEditor } from "./custom-components/custom-value-editor";
import { CustomRemoveRuleAction } from "./custom-components/custom-remove-rule-action";
import { CustomAddRuleAction } from "./custom-components/custom-add-rule-action";
import { CustomLockRuleAction } from "./custom-components/custom-lock-rule-action";
import { CustomRemoveGroupAction } from "./custom-components/custom-remove-group-action";
import { CustomLockGroupAction } from "./custom-components/custom-lock-group-action";
import { CustomCloneRuleAction } from "./custom-components/custom-clone-rule-action";
import { CustomCloneGroupAction } from "./custom-components/custom-clone-group-action";
import { CustomAddGroupAction } from "./custom-components/custom-add-group-action";
import { COMBINATORS, OperatorType } from "./constants";
import { queryBuilderStyles } from "./styles";
import { didTheFiltersChange, doesAllFiltersHaveValues, getDefaultField, getDefaultValue } from "./utils";
import { IFilterRule } from "./types";
import { DATATEST } from "constants/integration";

interface IProps {
    onApply: (rules: RuleType[]) => void;
    onRequestClose: () => void;
    fields: Field[];
    initialRules: RuleType[];
}

export const FiltersQueryBuilder: React.FC<IProps> = ({ initialRules, fields, onApply, onRequestClose }) => {
    const [query, setQuery] = React.useState<RuleGroupType>({
        combinator: OperatorType.AND,
        rules: initialRules,
    });
    const styles = queryBuilderStyles();
    const controlElements = {
        addGroupAction: CustomAddGroupAction,
        addRuleAction: CustomAddRuleAction,
        cloneGroupAction: CustomCloneGroupAction,
        cloneRuleAction: CustomCloneRuleAction,
        fieldSelector: CustomFieldSelector,
        lockRuleAction: CustomLockRuleAction,
        lockGroupAction: CustomLockGroupAction,
        removeGroupAction: CustomRemoveGroupAction,
        removeRuleAction: CustomRemoveRuleAction,
        valueEditor: CustomValueEditor,
    };
    const handleQueryChange = React.useCallback(
        (query: RuleGroupType) => {
            setQuery(query);
        },
        [setQuery]
    );

    const handleApply = React.useCallback(() => {
        onApply(query.rules as RuleType[]);
    }, [query.rules, onApply]);

    const handleCancel = React.useCallback(() => {
        setQuery({
            combinator: query.combinator,
            rules: initialRules,
        });

        onRequestClose();
    }, [query.combinator, initialRules, onRequestClose]);

    const noRulesAdded = query.rules.length == 0;

    const isApplyDisabled = React.useMemo(() => {
        if (!doesAllFiltersHaveValues(query.rules as IFilterRule[])) {
            return true;
        }
        return !didTheFiltersChange(initialRules, query.rules as IFilterRule[]);
    }, [query.rules, initialRules]);

    const handleGetDefaultField = () => {
        return getDefaultField(query.rules as RuleType[], fields);
    };

    return (
        <Theme>
            <Fabric>
                <div {...styles}>
                    {noRulesAdded && (
                        <p data-test={DATATEST.ASSORTMENTS.DIMENSIONS_FILTER.NO_FILTERS} className="noFilters">
                            {formatMessage("No filters applied. Please select Add Filter.")}
                        </p>
                    )}
                    <QueryBuilder
                        context={{ query }}
                        controlElements={controlElements}
                        fields={fields}
                        query={query}
                        onQueryChange={handleQueryChange}
                        combinators={COMBINATORS}
                        getDefaultValue={getDefaultValue}
                        getDefaultField={handleGetDefaultField}
                    />
                    <div className="actions">
                        <Button data-test={DATATEST.DIALOG.CANCEL} onClick={handleCancel}>
                            {formatMessage("Cancel")}
                        </Button>
                        <Button
                            data-test={DATATEST.DIALOG.APPLY}
                            primary
                            onClick={handleApply}
                            disabled={isApplyDisabled}
                        >
                            {formatMessage("Apply")}
                        </Button>
                    </div>
                </div>
            </Fabric>
        </Theme>
    );
};
